/**
 * RF-11.2 · Reporte de avance de una ejecución (`/api/ejecuciones/{id}/avance`).
 *
 * Alimenta el tablero F-04 con el porcentaje de indicadores calificados y el
 * semáforo de evidencias por factor.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError, getAuthToken } from '@/services/http'

// RF-11.2 · Reporte de avance del proceso (dashboard F-04).
// GET /api/ejecuciones/{id}/avance  ·  GET .../avance/pdf  (permiso procesos.ver)

export interface Semaforo {
  verde: number
  amarillo: number
  rojo: number
}

/** Forma con la que el API de avance proceso devuelve este dato. */
export interface PctFichas {
  documental: number | null
  cuantitativa: number | null
  opinion: number | null
}

/** Forma con la que el API de avance proceso devuelve este dato. */
export interface PendienteAvance {
  id_indicador: number
  enunciado: string
  caracteristica: string
}

/** Forma con la que el API de avance proceso devuelve este dato. */
export interface AvanceFactor {
  id_componente: number
  nombre: string
  lider: { id: number; name: string } | null
  total_indicadores: number
  calificados: number
  pct_calificados: number | null
  pct_fichas: PctFichas
  semaforo_documental: Semaforo
  pendientes: PendienteAvance[]
}

/** Forma con la que el API de avance proceso devuelve este dato. */
export interface AvanceProceso {
  id_ejecucion: number
  periodo: string
  estado: string
  programa: string | null
  campus: string | null
  global: {
    total_indicadores: number
    calificados: number
    pct_calificados: number | null
    pct_fichas: PctFichas
    semaforo_documental: Semaforo
  }
  factores: AvanceFactor[]
  generado_en: string
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

/**
 * Consulta avance proceso · `GET /api/ejecuciones`.
 *
 * @returns Resultado<AvanceProceso>
 */
export async function getAvanceProcesoService(idEjecucion: number): Promise<Resultado<AvanceProceso>> {
  const r = await http.get<AvanceProceso>(`/api/ejecuciones/${idEjecucion}/avance`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el avance del proceso') }
}

/** Descarga binaria del reporte de avance en PDF (fetch directo con el token de sesión). */
export async function descargarPdfAvanceService(idEjecucion: number): Promise<Resultado<null>> {
  const base = process.env.VUE_APP_API_BASE_URL ?? ''
  const token = getAuthToken()
  const res = await fetch(`${base}/api/ejecuciones/${idEjecucion}/avance/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  })
  if (!res.ok) {
    return { ok: false, status: res.status, error: 'No se pudo descargar el PDF del reporte de avance' }
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `avance-proceso-${idEjecucion}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  return { ok: true, status: res.status, data: null }
}
